import React, { useState, useEffect } from 'react';
import { createApiKey, getApiKeys, revokeApiKey } from '../api';
import { trackApiKeyCreated } from '../utils/analytics';
import { CheckIcon, ClipboardIcon, DocumentTextIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { Link } from "@tanstack/react-router";
import Breadcrumbs from '../components/Breadcrumbs';
import PageHeader from '../app/ui/PageHeader';
import { Card, CardHeader, CardContent } from '../app/ui/Card';
import { Button } from '../app/ui/Button';
import { Input } from '../app/ui/Input';

function ApiKeysPage() {
  const [keys, setKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyValue, setNewKeyValue] = useState(null); // Store the newly created key value temporarily
  const [copiedKey, setCopiedKey] = useState(false); // Track if the *new* key was just copied
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createError, setCreateError] = useState(null);
  const [revokeError, setRevokeError] = useState({}); // Store errors per key ID

  const crumbs = [
    { label: 'Dashboard', to: '/app/dashboard' },
    { label: 'API keys' }
  ];

  // Fetch keys on component mount
  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = async () => {
    setLoading(true);
    setError(null);
    setRevokeError({});
    try {
      const fetchedKeys = await getApiKeys();
      setKeys(fetchedKeys);
    } catch (err) {
      setError(`Error fetching API keys: ${err.message}`);
      console.error("Fetch Keys Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) {
      setCreateError('Key name cannot be empty.');
      return;
    }
    setCreateError(null);
    setNewKeyValue(null); // Clear previous new key value
    setLoading(true); // Indicate loading during creation
    try {
      const createdKey = await createApiKey(newKeyName);
      setNewKeyValue(createdKey.key); // Show the key value
      trackApiKeyCreated();
      setNewKeyName(''); // Clear input field
      await fetchKeys(); // Refresh the list (await to ensure loading state correct)
    } catch (err) {
      setCreateError(`Error creating API key: ${err.message}`);
      console.error("Create Key Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }
    setRevokeError({ ...revokeError, [keyId]: null }); // Clear previous error for this key
    // Optionally indicate loading state for the specific row
    try {
      await revokeApiKey(keyId);
      await fetchKeys(); // Refresh the list
    } catch (err) {
      setRevokeError({ ...revokeError, [keyId]: `Error revoking key: ${err.message}` });
      console.error(`Revoke Key Error (${keyId}):`, err);
    }
  };

  const copyToClipboard = (text) => {
    setCopiedKey(false); // Reset copy status if called again quickly
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(true);
      // Reset the copied status after a short delay
      setTimeout(() => setCopiedKey(false), 2000); // Hide after 2 seconds
    }, (err) => {
      // Handle copy failure - maybe show an error icon briefly?
      // For now, just log the error
      console.error('Clipboard copy error:', err);
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      // Format manually to yyyy-MM-dd HH:mm:ss
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (err) {
      console.error("Date formatting error:", err);
      return 'Invalid Date';
    }
  };

  return (
    <div className="space-y-6">
      <Breadcrumbs crumbs={crumbs} />

      <PageHeader
        title="API keys"
        subtitle="Create and manage API keys to authenticate requests to the compression API"
      />

      {/* API Documentation Reference */}
      <Card>
        <CardContent>
          <div className="flex items-start gap-3">
            <DocumentTextIcon className="h-6 w-6 text-accent flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-foreground">API documentation</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Learn how to use your API keys with the compression API. View examples, endpoints,
                and integration guides in the comprehensive documentation.
              </p>
              <Link
                to="/docs/api"
                className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-accent hover:text-accent/90 transition-colors"
              >
                View API documentation
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create New Key Section */}
      <Card>
        <CardHeader
          title="Create new API key"
          subtitle="Generate a new API key for your application"
        />
        <CardContent>
          <form onSubmit={handleCreateKey} className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Input
                  type="text"
                  id="keyName"
                  label="Key name"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., Production API key"
                  required
                  disabled={loading}
                />
              </div>
              <div className="sm:pt-6">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading}
                >
                  {loading && !newKeyValue ? 'Creating...' : 'Create key'}
                </Button>
              </div>
            </div>
            {createError && (
              <p className="text-sm text-danger">{createError}</p>
            )}
          </form>

          {newKeyValue && (
            <div className="mt-6 p-4 bg-accent/5 border border-accent/20 rounded-xl">
              <div className="flex items-start gap-2 mb-3">
                <CheckIcon className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-foreground">API key created successfully!</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Please copy your new API key. You won't be able to see it again.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-background-soft p-3 rounded-lg border border-border/50">
                <code className="text-sm text-foreground break-all flex-1 font-mono">
                  {newKeyValue}
                </code>
                <Button
                  size="sm"
                  variant={copiedKey ? "accent" : "secondary"}
                  onClick={() => copyToClipboard(newKeyValue)}
                  disabled={copiedKey}
                >
                  {copiedKey ? (
                    <>
                      <CheckIcon className="h-4 w-4 mr-1.5" />
                      Copied
                    </>
                  ) : (
                    <>
                      <ClipboardIcon className="h-4 w-4 mr-1.5" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Existing Keys Section */}
      <Card>
        <CardHeader
          title="Your API keys"
          subtitle="Manage and monitor your active API keys"
        />
        <CardContent>
          {loading && keys.length === 0 && (
            <p className="text-muted-foreground">Loading keys...</p>
          )}
          {error && (
            <p className="text-danger">{error}</p>
          )}
          {!loading && !error && keys.length === 0 && (
            <div className="text-center py-12">
              <DocumentTextIcon className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">You haven't created any API keys yet.</p>
              <p className="text-sm text-muted-foreground mt-1">Create your first API key to get started.</p>
            </div>
          )}
          {!loading && !error && keys.length > 0 && (
            <div className="overflow-x-auto -mx-6">
              <table className="min-w-full divide-y divide-border/50">
                <thead>
                  <tr className="bg-background-soft/50">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Created
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Last used
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {keys.map((key) => (
                    <tr key={key.id} className="hover:bg-background-soft/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm font-medium text-foreground">{key.name}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-muted-foreground">{formatDate(key.created_at)}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-muted-foreground">{formatDate(key.last_used_at)}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {key.is_active ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/20">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-danger/10 text-danger border border-danger/20">
                            Revoked
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        {key.is_active && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRevokeKey(key.id)}
                            className="text-danger hover:text-danger hover:bg-danger/10"
                          >
                            Revoke
                          </Button>
                        )}
                        {revokeError[key.id] && (
                          <p className="text-danger text-xs mt-1">{revokeError[key.id]}</p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ApiKeysPage;